<template>
  <div class="content">
    <div class="container-fluid">

      <!-- Top Stats Row -->
      <div class="row mt-3">
        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-warning">
              <b-icon style="color: rgba(240, 206, 13, 0.937)" icon="person-fill" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Total Users</p>
              <h4 class="card-title">{{ stats.userStatistics.totalUsers || 0 }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="person-check-fill" aria-hidden="true"></b-icon>
              Active: {{ stats.userStatistics.activeUsers || 0 }} &nbsp;|&nbsp;
              <b-icon icon="lock-fill" aria-hidden="true"></b-icon>
              Locked: {{ stats.userStatistics.lockedUsers || 0 }}
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-success">
              <b-icon style="color: rgb(37, 91, 48)" icon="building" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Buildings</p>
              <h4 class="card-title">{{ stats.buildingStatistics.totalBuildings || 0 }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="check-circle-fill" aria-hidden="true"></b-icon>
              Active: {{ stats.buildingStatistics.activeBuildings || 0 }} &nbsp;|&nbsp;
              Inactive: {{ stats.buildingStatistics.inactiveBuildings || 0 }}
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-info">
              <b-icon style="color: rgba(37, 91, 48)" icon="house-fill" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Houses</p>
              <h4 class="card-title">{{ stats.houseStatistics.totalHouses || 0 }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="house-door-fill" aria-hidden="true"></b-icon>
              Rented: {{ stats.houseStatistics.rentedHouses || 0 }} &nbsp;|&nbsp;
              Free: {{ stats.houseStatistics.freeHouses || 0 }}
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="person">
              <b-icon style="color: rgba(37, 91, 48)" icon="people-fill" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Tenants</p>
              <h4 class="card-title">{{ stats.tenantStatistics.totalTenants || 0 }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="person-check-fill" aria-hidden="true"></b-icon>
              Active: {{ stats.tenantStatistics.activeTenants || 0 }} &nbsp;|&nbsp;
              Inactive: {{ stats.tenantStatistics.inactiveTenants || 0 }}
            </div>
          </stats-card>
        </div>
      </div>

      <!-- Contract & Payment Summary Row -->
      <div class="row mt-2">
        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-warning">
              <b-icon style="color: #255b30" icon="file-earmark-text-fill" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Contracts</p>
              <h4 class="card-title">{{ stats.contractStatistics.totalContracts || 0 }}</h4>
            </div>
            <div slot="footer">
              Active: {{ stats.contractStatistics.activeContracts || 0 }} &nbsp;|&nbsp;
              Expiring Soon: <span class="text-warning">{{ stats.contractStatistics.expiringSoon || 0 }}</span>
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-success">
              <b-icon style="color: #255b30" icon="cash-stack" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Today's Collection</p>
              <h4 class="card-title">{{ numberWithCommas(stats.paymentStatistics.dailyTotal) }}</h4>
            </div>
            <div slot="footer">
              Payments today: {{ stats.paymentStatistics.todayPaymentCount || 0 }}
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-info" style="position: relative; display: inline-flex; align-items: center; justify-content: center;">
              <b-icon style="color: #255b30" icon="calendar" aria-hidden="true"></b-icon>
              <span style="position: absolute; bottom: 3px; font-size: 25px; font-weight: bold; color: #255b30; line-height: 1; pointer-events: none;">
                {{ new Date().toLocaleString('default', { month: 'short' }).toUpperCase() }}
              </span>
            </div>
            <div slot="content">
              <p class="card-category">Monthly Collection</p>
              <h4 class="card-title">{{ numberWithCommas(stats.paymentStatistics.monthlyTotal) }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="calendar-week" aria-hidden="true"></b-icon>
              Weekly: {{ numberWithCommas(stats.paymentStatistics.weeklyTotal) }}
            </div>
          </stats-card>
        </div>

        <div class="col-xl-3 col-md-6">
          <stats-card>
            <div slot="header" class="icon-success">
              <b-icon style="color: #1a7a3a" icon="coin" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category">Collected Rent Amount</p>
              <h4 class="card-title" style="color: #1a7a3a;">{{ numberWithCommas(stats.financialSummary.totalRentCollected) }}</h4>
            </div>
            <div slot="footer">
              <b-icon icon="check-circle-fill" aria-hidden="true"></b-icon>
              All rent payments collected
            </div>
          </stats-card>
        </div>
      </div>

      <!-- Financial Status Row -->
      <div class="row mt-2">
        <!-- Total Collection Card - Redesigned -->
        <div class="col-xl-4 col-md-6">
          <stats-card>
            <div slot="header" style="position: relative; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #255b30, #1a7a3a); padding: 12px; border-radius: 50%; width: 60px; height: 60px;">
              <b-icon style="color: white; font-size: 28px;" icon="piggy-bank-fill" aria-hidden="true"></b-icon>
              <span style="position: absolute; bottom: -8px; right: -8px; background: #F0CE0D; color: #255b30; font-size: 16px; font-weight: bold; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border: 2px solid white;">
                $
              </span>
            </div>
            <div slot="content">
              <p class="card-category" style="font-weight: 600;">Total Collection</p>
              <h4 class="card-title" style="color: #1a7a3a; font-weight: 700; font-size: 28px;">
                {{ numberWithCommas(stats.financialSummary.netIncome) }}
              </h4>
            </div>
            <div slot="footer" style="display: flex; justify-content: space-between; align-items: center;">
              <span>
                <b-icon icon="arrow-up-circle-fill" style="color: #1a7a3a;" aria-hidden="true"></b-icon>
                Gross: {{ numberWithCommas(stats.financialSummary.grossIncome) }}
              </span>
              <span class="badge badge-success" style="background: #1a7a3a; padding: 5px 12px; border-radius: 20px;">
                <b-icon icon="cash" aria-hidden="true"></b-icon>
                Net
              </span>
            </div>
          </stats-card>
        </div>

        <!-- Total Unpaid Amount -->
        <div class="col-xl-4 col-md-6">
          <stats-card>
            <div slot="header" style="position: relative; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #f0ad4e, #d4a017); padding: 12px; border-radius: 50%; width: 60px; height: 60px;">
              <b-icon style="color: white; font-size: 28px;" icon="exclamation-triangle-fill" aria-hidden="true"></b-icon>
            </div>
            <div slot="content">
              <p class="card-category" style="font-weight: 600;">Total Unpaid Amount</p>
              <h4 class="card-title" style="color: #d4a017; font-weight: 700; font-size: 28px;">
                {{ numberWithCommas(stats.contractStatistics.totalUnpaidAmount) }}
              </h4>
            </div>
            <div slot="footer">
              <b-icon icon="clock-fill" aria-hidden="true"></b-icon>
              Unpaid: {{ stats.contractStatistics.unpaidContracts || 0 }} &nbsp;|&nbsp;
              <b-icon icon="exclamation-circle-fill" aria-hidden="true"></b-icon>
              Overdue: {{ stats.contractStatistics.overdueContracts || 0 }}
            </div>
          </stats-card>
        </div>

        <!-- Total Overdue Amount -->
        <div class="col-xl-4 col-md-6">
          <stats-card>
            <div slot="header" style="position: relative; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #dc3545, #b02a37); padding: 12px; border-radius: 50%; width: 60px; height: 60px;">
              <b-icon style="color: white; font-size: 28px;" icon="exclamation-octagon-fill" aria-hidden="true"></b-icon>
              <span style="position: absolute; top: -5px; right: -5px; background: #fff; color: #dc3545; font-size: 14px; font-weight: bold; border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; border: 2px solid #dc3545;">
                !
              </span>
            </div>
            <div slot="content">
              <p class="card-category" style="font-weight: 600;">Total Overdue Amount</p>
              <h4 class="card-title" style="color: #dc3545; font-weight: 700; font-size: 28px;">
                {{ numberWithCommas(stats.contractStatistics.totalOverdueAmount) }}
              </h4>
            </div>
            <div slot="footer">
              <b-icon icon="calendar-x-fill" aria-hidden="true"></b-icon>
              Overdue contracts: {{ stats.contractStatistics.overdueContracts || 0 }}
            </div>
          </stats-card>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="row mt-2">
        <!-- Contract Payment Status Pie -->
        <div class="col-md-4">
          <b-card style="display: flex; justify-content: center; align-items: center;">
            <apexchart width="500" type="pie" :options="pieChart.chartOptions" :series="pieChart.series"></apexchart>
          </b-card>
        </div>

        <!-- Monthly Revenue Bar -->
        <div class="col-md-8">
          <b-card style="display: flex; justify-content: center; align-items: center;">
            <div class="row" style="display: flex; justify-content: end; align-items: end;">
              <b-form-select style="width: 20%;" v-model="currentYear" :options="yearOptions" size="sm" @change="buildBarChart" class="mt-3"></b-form-select>
            </div>
            <div class="row">
              <apexchart width="700" height="350" type="bar" :options="barChart.chartOptions" :series="barChart.series"></apexchart>
            </div>
          </b-card>
        </div>
      </div>

      <!-- User Roles & Financial Detail Row -->
      <div class="row mt-2">
        <!-- User Role Breakdown -->
        <div class="col-md-4">
          <b-card>
            <h6 class="card-title text-center" style="color:#9A9A9A; font-weight:400;">User Roles</h6>
            <table class="table table-sm">
              <tbody>
                <tr><td>Owner</td><td class="text-right">{{ stats.userStatistics.ownerCount || 0 }}</td></tr>
                <tr><td>Manager</td><td class="text-right">{{ stats.userStatistics.managerCount || 0 }}</td></tr>
                <tr><td>Finance Manager</td><td class="text-right">{{ stats.userStatistics.financeManagerCount || 0 }}</td></tr>
                <tr><td>Maker</td><td class="text-right">{{ stats.userStatistics.makerCount || 0 }}</td></tr>
                <tr><td>Admin</td><td class="text-right">{{ stats.userStatistics.adminCount || 0 }}</td></tr>
                <tr><td>Super Admin</td><td class="text-right">{{ stats.userStatistics.superAdminCount || 0 }}</td></tr>
              </tbody>
            </table>
          </b-card>
        </div>

        <!-- Financial Summary -->
        <div class="col-md-4">
          <b-card>
            <h6 class="card-title text-center" style="color:#9A9A9A; font-weight:400;">Financial Summary</h6>
            <table class="table table-sm">
              <tbody>
                <tr><td>Rent</td><td class="text-right">{{ numberWithCommas(stats.financialSummary.totalRentCollected) }}</td></tr>
                <tr><td>Guaranteed</td><td class="text-right">{{ numberWithCommas(stats.financialSummary.totalGuaranteedCollected) }}</td></tr>
                <tr><td>Bills</td><td class="text-right">{{ numberWithCommas(stats.financialSummary.totalBillsCollected) }}</td></tr>
                <tr><td>Penalty</td><td class="text-right">{{ numberWithCommas(stats.financialSummary.totalPenaltyCollected) }}</td></tr>
                <tr><td>VAT</td><td class="text-right"> {{ numberWithCommas(stats.financialSummary.totalVat) }}</td></tr>
                <tr><td>Withholding</td><td class="text-right text-danger">- {{ numberWithCommas(stats.financialSummary.totalWithHolding) }}</td></tr>
                <tr class="font-weight-bold"><td>Total Collection</td><td class="text-right text-success">{{ numberWithCommas(stats.financialSummary.netIncome) }}</td></tr>
              </tbody>
            </table>
          </b-card>
        </div>

        <!-- Contract Status -->
        <div class="col-md-4">
          <b-card>
            <h6 class="card-title text-center" style="color:#9A9A9A; font-weight:400;">Contract Status</h6>
            <table class="table table-sm">
              <tbody>
                <tr><td>Active</td><td class="text-right text-success">{{ stats.contractStatistics.activeContracts || 0 }}</td></tr>
                <tr><td>Terminated</td><td class="text-right text-danger">{{ stats.contractStatistics.terminatedContracts || 0 }}</td></tr>
                <tr><td>Paid</td><td class="text-right text-success">{{ stats.contractStatistics.paidContracts || 0 }}</td></tr>
                <tr><td>Unpaid</td><td class="text-right text-warning">{{ stats.contractStatistics.unpaidContracts || 0 }}</td></tr>
                <tr><td>Overdue</td><td class="text-right text-danger">{{ stats.contractStatistics.overdueContracts || 0 }}</td></tr>
                <tr><td>Expiring Today</td><td class="text-right text-warning">{{ stats.contractStatistics.expiringToday || 0 }}</td></tr>
                <tr><td>Expiring in 30 days</td><td class="text-right text-warning">{{ stats.contractStatistics.expiringSoon || 0 }}</td></tr>
              </tbody>
            </table>
          </b-card>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
  import StatsCard from 'src/components/Cards/StatsCard.vue'
  import ChartCard from 'src/components/Cards/ChartCard.vue'
  import LTable from 'src/components/Table.vue'

  const emptyStats = {
    userStatistics: {},
    buildingStatistics: {},
    houseStatistics: {},
    tenantStatistics: {},
    contractStatistics: {},
    paymentStatistics: {},
    financialSummary: { monthlyBreakdown: [] },
  };

  export default {
    components: { LTable, ChartCard, StatsCard },
    data() {
      return {
        stats: JSON.parse(JSON.stringify(emptyStats)),
        currentYear: new Date().getFullYear(),
        yearOptions: Array.from({ length: 11 }, (_, i) => ({ text: String(2022 + i), value: 2022 + i })),
        pieChart: {
          chartOptions: {
            chart: { id: 'super-pie' },
            title: { text: 'Contract Payment Status', align: 'center', style: { fontSize: '16px', fontWeight: '400', color: '#9A9A9A' } },
            colors: ['#255b30', '#F0CE0D', '#FB404B'],
            labels: ['Paid', 'Unpaid', 'Overdue'],
            legend: { show: true, position: 'bottom' },
          },
          series: [0, 0, 0],
        },
        barChart: {
          chartOptions: {
            chart: { id: 'super-bar' },
            plotOptions: { bar: { distributed: true } },
            legend: { show: false },
            title: { text: 'Monthly Revenue', align: 'center', margin: 40, style: { fontSize: '16px', fontWeight: '400', color: '#9A9A9A' } },
            xaxis: { categories: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] },
            colors: Array.from({ length: 12 }, (_, i) => i === new Date().getMonth() ? '#F0CE0D' : '#255b30'),
          },
          series: [{ name: 'Revenue', data: Array(12).fill(0) }],
        },
      };
    },
    mounted() {
      this.loadDashboard();
    },
    methods: {
      async loadDashboard() {
        try {
          const response = await this.$store.dispatch('getAdminDashboardStatistics');
          this.stats = response.data;
          this.buildPieChart();
          this.buildBarChart();
        } catch (error) {
          console.error('Dashboard load error:', error);
        }
      },
      buildPieChart() {
        const c = this.stats.contractStatistics;
        this.pieChart.series = [
          c.paidContracts || 0,
          c.unpaidContracts || 0,
          c.overdueContracts || 0,
        ];
      },
      buildBarChart() {
        const breakdown = this.stats.financialSummary.monthlyBreakdown || [];
        const months = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
        const filtered = breakdown.filter(m => m.year === this.currentYear);
        const data = months.map(mn => {
          const found = filtered.find(m => m.month === mn);
          return found ? found.total : 0;
        });
        const isCurrentYear = this.currentYear === new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const colors = Array.from({ length: 12 }, (_, i) =>
          isCurrentYear && i === currentMonth ? '#F0CE0D' : '#255b30'
        );
        this.barChart = {
          ...this.barChart,
          chartOptions: { ...this.barChart.chartOptions, colors },
          series: [{ name: 'Revenue', data }],
        };
        window.dispatchEvent(new Event('resize'));
      },
      numberWithCommas(x) {
        if (x == null) return '0.00';
        return parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
    },
  };
</script>

<style>
  .ct-series-b .ct-slice-pie, .ct-series-b .ct-area { fill: rgba(240, 206, 13, 0.973); }
  .ct-series-a .ct-slice-pie, .ct-series-a .ct-area { fill: rgb(37, 91, 48, 0.6); }
  .ct-series-c .ct-slice-pie, .ct-series-c .ct-area { fill: #FB404B !important; }
</style>